import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { StampSerial, StatusBadge, RM } from "@/components/shared";
import { Loader2, Wallet, User, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between py-2.5 border-b border-hairline">
      <span className="text-xs uppercase tracking-wider text-slate">{label}</span>
      <span className={`text-sm text-ink text-right ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate/40">—</span>}</span>
    </div>
  );
}

export default function MemberPortal() {
  const [d, setD] = useState(null);
  const [empty, setEmpty] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const load = () => api.get("/members/me").then((r) => setD(r.data)).catch(() => setEmpty(true));
  useEffect(() => { load(); }, []);

  if (empty) return (
    <Layout title="Profil Saya / My Profile">
      <div className="bg-white border border-hairline rounded-sm py-16 px-6 text-center" data-testid="portal-empty">
        <User size={36} className="text-brass mx-auto mb-3" />
        <h3 className="font-serif text-xl text-ink">Tiada rekod ahli dipautkan</h3>
        <p className="text-sm text-slate mt-2">No member record is linked to this account yet. Please contact the secretariat.</p>
      </div>
    </Layout>
  );
  if (!d) return <Layout title="Profil Saya / My Profile"><div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div></Layout>;
  const m = d.member;

  return (
    <Layout title="Profil Saya / My Profile" subtitle={<span className="flex items-center gap-2"><StampSerial value={m.membership_no} /> <StatusBadge status={m.status} /></span>}>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-hairline rounded-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><User size={18} className="text-brass" /><h3 className="font-serif text-lg text-ink">Maklumat Saya / My Details</h3></div>
            <button onClick={() => setEditOpen(true)} data-testid="portal-edit-btn" className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-1.5 rounded-sm text-sm transition-colors"><Pencil size={14} /> Kemas Kini / Update</button>
          </div>
          <div className="grid md:grid-cols-2 gap-x-8">
            <div>
              <Row label="Nama / Name" value={`${m.title !== "(none)" ? m.title + " " : ""}${m.name}`} />
              <Row label="No. Ahli / Membership" value={<StampSerial value={m.membership_no} />} />
              <Row label="I.C. No" value={m.ic_display} mono />
              <Row label="Tarikh Lahir / DOB" value={m.dob} mono />
            </div>
            <div>
              <Row label="Emel / Email" value={m.email} />
              <Row label="Mobil / Mobile" value={m.mobile} mono />
              <Row label="Tarikh Sertai / Join Date" value={m.join_date} mono />
              <Row label="Alamat / Address" value={m.address} />
            </div>
          </div>
          <p className="text-xs text-slate mt-4 italic">Anda boleh kemas kini emel, mobil & alamat sendiri. Perubahan nama/IC perlu hubungi urus setia.</p>
        </div>

        <div className="space-y-6">
          <div className="bg-ink text-paper rounded-sm p-6">
            <div className="flex items-center gap-2 mb-3"><Wallet size={16} className="text-brass" /><h3 className="font-serif text-lg text-paper">Modal Syer / My Shares</h3></div>
            <div className="font-serif text-4xl"><span className="text-lg text-paper/60 mr-1">RM</span><RM value={m.share_balance} className="text-paper" /></div>
            <div className="text-xs text-paper/50 mt-2">{m.opening_balance_confirmed ? "Confirmed balance" : "Pending confirmation"}</div>
          </div>

          <div className="bg-white border border-hairline rounded-sm">
            <div className="px-6 py-4 border-b border-hairline"><h3 className="font-serif text-lg text-ink">Transaksi / Ledger</h3></div>
            <div className="divide-y divide-hairline">
              {d.transactions.length === 0 ? <div className="px-6 py-6 text-sm text-slate">Tiada transaksi.</div> :
                d.transactions.map((t) => (
                  <div key={t.id} className="px-6 py-3 flex justify-between items-center">
                    <div><div className="text-sm text-ink capitalize">{t.type.replace("_", " ")}</div><div className="text-[10px] text-slate font-mono">{(t.created_at || "").slice(0, 10)}</div></div>
                    <div className="text-right"><div className="text-sm"><RM value={t.amount} /></div><div className="text-[10px] text-slate">bal <RM value={t.balance_after} /></div></div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      {editOpen && <SelfEditModal member={m} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />}
    </Layout>
  );
}

function SelfEditModal({ member, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: member.email || "", mobile: member.mobile || "", phone_office: member.phone_office || "",
    address: member.address || "", correspondence_address: member.correspondence_address || "", occupation: member.occupation || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try { await api.put("/profile", form); toast.success("Maklumat dikemas kini / Profile updated"); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const field = (k, label, type = "text") => (
    <div><label className="block text-xs font-medium text-slate mb-1">{label}</label>
      <input type={type} value={form[k]} onChange={set(k)} data-testid={`portal-${k}`} className="w-full bg-white border border-slate-300 rounded-sm px-3 py-2 text-sm outline-none focus:border-brass text-ink" /></div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(18,42,67,0.4)" }} onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-lg animate-fade-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline"><h3 className="font-serif text-xl text-ink">Kemas Kini Maklumat / Update Details</h3><button onClick={onClose} className="text-slate hover:text-ink"><X size={20} /></button></div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          {field("email", "Emel / Email", "email")}
          {field("mobile", "Mobil / Mobile")}
          {field("phone_office", "Tel (Pej) / Office")}
          {field("occupation", "Pekerjaan / Occupation")}
          <div className="md:col-span-2">{field("address", "Alamat / Address")}</div>
          <div className="md:col-span-2">{field("correspondence_address", "Alamat Surat / Correspondence")}</div>
        </div>
        <div className="px-6 py-4 border-t border-hairline flex justify-end gap-3">
          <button onClick={onClose} className="bg-transparent border border-ink/20 text-ink px-4 py-2 rounded-sm text-sm">Batal</button>
          <button onClick={save} disabled={saving} data-testid="portal-save" className="bg-ink text-white hover:bg-ink-light px-4 py-2 rounded-sm text-sm flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}Simpan</button>
        </div>
      </div>
    </div>
  );
}
